import { Link } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/AdminLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Users, Calendar, Scissors, CheckCircle2, Circle, ArrowLeft, Copy, RotateCcw, Ban, UserRoundCog, MessageSquareText } from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface Props {
  organization: {
    id: number
    name: string
    slug: string
    created_at: string
    subscription_status: string
    subscription_plan: string
    stripe_subscription_id: string | null
  }
  members: Array<{
    id: number
    name: string
    phone: string
    email: string | null
    role: string
  }>
  pendingStaffInvites: Array<{
    id: number
    name: string
    phone: string | null
    role: string
    business_name: string
    expired: boolean
    invite_token: string | null
    invite_expires_at: string | null
  }>
  services: Array<{
    id: number
    name: string
    duration_minutes: number
    price_cents: number
  }>
  recentBookings: Array<{
    id: number
    client_name: string | null
    service_name: string | null
    starts_at: string
    status: string
  }>
  stats: {
    total_bookings: number
    total_clients: number
    total_services: number
  }
  onboarding: {
    steps: Array<{ name: string; completed: boolean }>
    complete: boolean
    dismissed: boolean
    next_step: string | null
  }
  ownerInvite: {
    id: number
    status: 'pending' | 'accepted' | 'expired' | 'revoked' | 'superseded'
    token: string | null
    owner_id: number
    owner_name: string
    owner_phone: string
    expires_at: string
    last_sent_at: string
  } | null
}

const STEP_LABELS: Record<string, string> = {
  services: 'Servicios',
  demo_conversation: 'Prueba del agente',
  whatsapp: 'WhatsApp',
}

function planBadge(plan: string, status: string) {
  const planMap: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' | 'default' }> = {
    pro: { label: 'Pro', variant: 'default' },
    basic: { label: 'Basic', variant: 'secondary' },
    free: { label: 'Free', variant: 'secondary' },
  }
  const statusMap: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
    active: { label: 'Activo', variant: 'success' },
    trialing: { label: 'Trial', variant: 'warning' },
    canceled: { label: 'Cancelado', variant: 'destructive' },
    past_due: { label: 'Vencido', variant: 'destructive' },
  }
  const planInfo = planMap[plan] || { label: plan, variant: 'secondary' as const }
  const statusInfo = statusMap[status]
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant={planInfo.variant}>{planInfo.label}</Badge>
      {statusInfo && status !== 'active' && (
        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
      )}
    </div>
  )
}

function bookingStatusBadge(status: string) {
  const map: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
    confirmed: { label: 'Confirmada', variant: 'success' },
    cancelled: { label: 'Cancelada', variant: 'destructive' },
    completed: { label: 'Completada', variant: 'secondary' },
  }
  const info = map[status] || { label: status, variant: 'secondary' as const }
  return <Badge variant={info.variant}>{info.label}</Badge>
}

export default function Show({ organization, members, pendingStaffInvites = [], services, recentBookings, stats, onboarding, ownerInvite }: Props) {
  const inviteUrl = ownerInvite?.token ? `${window.location.origin}/owner-invite/${ownerInvite.token}` : null

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/admin/organizations">
              <ArrowLeft className="mr-1 h-4 w-4" /> Volver
            </Link>
          </Button>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{organization.name}</h1>
            <p className="text-sm text-muted-foreground">/{organization.slug} · Creado {new Date(organization.created_at).toLocaleDateString('es-MX')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              className={buttonVariants({ variant: 'outline' })}
              href={`/admin/organizations/${organization.id}/chatbot_playground`}
            >
              <MessageSquareText className="mr-2 h-4 w-4" />
              Probar chatbot
            </Link>
            {planBadge(organization.subscription_plan, organization.subscription_status)}
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard icon={Calendar} label="Citas totales" value={stats.total_bookings} />
          <StatCard icon={Users} label="Clientes" value={stats.total_clients} />
          <StatCard icon={Scissors} label="Servicios" value={stats.total_services} />
        </div>

        {ownerInvite && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Invitación de propietaria</CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">{ownerInvite.owner_name} · {ownerInvite.owner_phone}</p>
                </div>
                <Badge variant={ownerInvite.status === 'accepted' ? 'success' : ownerInvite.status === 'pending' ? 'warning' : 'secondary'}>
                  {{ pending: 'Pendiente', accepted: 'Aceptada', expired: 'Expirada', revoked: 'Revocada', superseded: 'Reemplazada' }[ownerInvite.status]}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {inviteUrl && (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input value={inviteUrl} readOnly aria-label="Enlace de invitación" />
                  <Button variant="outline" onClick={() => navigator.clipboard.writeText(inviteUrl)}><Copy className="mr-2 h-4 w-4" /> Copiar</Button>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {(ownerInvite.status === 'pending' || ownerInvite.status === 'expired') && (
                  <Link className={buttonVariants({ variant: 'outline' })} href={`/admin/organizations/${organization.id}/renew_invite`} method="post" as="button"><RotateCcw className="mr-2 h-4 w-4" /> Renovar enlace</Link>
                )}
                {ownerInvite.status === 'pending' && (
                  <>
                    <Link className={buttonVariants({ variant: 'outline' })} href={`/admin/organizations/${organization.id}/revoke_invite`} method="post" as="button"><Ban className="mr-2 h-4 w-4" /> Revocar</Link>
                  </>
                )}
                <Link className={buttonVariants()} href={`/admin/users/${ownerInvite.owner_id}/impersonate`} method="post" data={{ organization_id: organization.id }} as="button">
                  <UserRoundCog className="mr-2 h-4 w-4" /> Configurar como cliente
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {pendingStaffInvites.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Invitaciones de equipo ({pendingStaffInvites.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingStaffInvites.map((invite) => {
                    const staffInviteUrl = invite.invite_token ? `${window.location.origin}/invite/${invite.invite_token}` : null
                    return (
                      <TableRow key={invite.id}>
                        <TableCell>
                          <div className="font-medium">{invite.name}</div>
                          <div className="text-xs text-muted-foreground">{invite.business_name}</div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{invite.phone}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{invite.role === 'admin' ? 'Administrador' : 'Personal'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={invite.expired ? 'destructive' : 'warning'}>
                            {invite.expired ? 'Expirada' : 'Pendiente'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            {staffInviteUrl && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigator.clipboard.writeText(staffInviteUrl)}
                              >
                                <Copy className="mr-2 h-4 w-4" /> Copiar
                              </Button>
                            )}
                            <Link
                              className={buttonVariants({ variant: 'outline', size: 'sm' })}
                              href={`/admin/organizations/${organization.id}/renew_staff_invite`}
                              method="post"
                              data={{ staff_assignment_id: invite.id }}
                              as="button"
                            >
                              <RotateCcw className="mr-2 h-4 w-4" /> Renovar
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Onboarding */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Onboarding</CardTitle>
              {onboarding.complete ? (
                <Badge variant="success">Completado</Badge>
              ) : onboarding.dismissed ? (
                <Badge variant="secondary">Descartado</Badge>
              ) : (
                <Badge variant="warning">En progreso</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {onboarding.steps.map((step) => (
                <div key={step.name} className="flex items-center gap-1.5">
                  {step.completed ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Circle className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className={step.completed ? 'text-sm text-foreground' : 'text-sm text-muted-foreground'}>
                    {STEP_LABELS[step.name] || step.name}
                  </span>
                </div>
              ))}
            </div>
            {onboarding.next_step && !onboarding.dismissed && (
              <p className="mt-3 text-sm text-yellow-500">
                Siguiente paso: {STEP_LABELS[onboarding.next_step] || onboarding.next_step}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Members */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Miembros ({members.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        <Link href={`/admin/users/${m.id}`} className="font-medium hover:underline">
                          {m.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{m.phone}</TableCell>
                      <TableCell>
                        <Badge variant={m.role === 'owner' ? 'default' : 'secondary'}>{m.role}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {(m.role === 'owner' || m.role === 'admin') && (
                          <Link
                            className={buttonVariants({ variant: 'outline', size: 'sm' })}
                            href={`/admin/users/${m.id}/impersonate`}
                            method="post"
                            data={{ organization_id: organization.id }}
                            as="button"
                          >
                            <UserRoundCog className="h-4 w-4" />
                            Iniciar sesión
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Services */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Servicios ({services.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Duración</TableHead>
                    <TableHead>Precio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">Sin servicios</TableCell>
                    </TableRow>
                  ) : (
                    services.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell className="text-muted-foreground">{s.duration_minutes} min</TableCell>
                        <TableCell className="text-muted-foreground">${(s.price_cents / 100).toFixed(0)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Recent bookings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Citas recientes</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Servicio</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Sin citas</TableCell>
                  </TableRow>
                ) : (
                  recentBookings.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{b.client_name || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{b.service_name || '—'}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(b.starts_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                      </TableCell>
                      <TableCell>{bookingStatusBadge(b.status)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: number
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}
